import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
// logo is served from public/images; use Loader component only
import Loader from "../components/Loader"; // spinner component

// lazy-load pages to trigger Suspense fallback
const Shop = lazy(
  () =>
    new Promise((resolve) =>
      setTimeout(() => resolve(import("../pages/shop/Shop")), 300)
    )
);
const CategoryPage = lazy(() => import("../pages/shop/Category"));
const SearchResults = lazy(() => import("../pages/shop/SearchResults"));
const ProductPage = lazy(() => import("../pages/shop/Product"));
const Checkout = lazy(() => import("../components/Stepper/Checkout"));
const Cart = lazy(() => import("../components/Stepper/Cart"));
const StepSuccess = lazy(() => import("../components/Stepper/StepSuccess"));
const Signup = lazy(() => import("../components/Auth/Signup"));
const ForgotPassword = lazy(() => import("../components/Auth/ForgetPassword"));
const ResetPassword = lazy(() => import("../components/Auth/ResetPassword"));
const Myaccount = lazy(() => import("../components/Myaccount"));
const TermsConditions = lazy(() => import("../Helper/TermsConditions"));
const PrivacyAndPolicy = lazy(() => import("../Helper/privacyandpolicy"));
const ReturnRefundPolicy = lazy(() => import("../Helper/Refund policy"));
const ShippingPolicy = lazy(() => import("../Helper/ShippingPolicy"));
const WORDPRESS_LOGIN_URL = "https://ammachekku.com/log-in/";
// About, ContactUs, Home removed because they are not present in srcroot

// helper component for sending users to an external URL
const ExternalRedirect = ({ url }) => {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);
  return null;
};

const WordpressLoginRedirect = () => {
  React.useEffect(() => {
    const loginUrl = new URL(WORDPRESS_LOGIN_URL);
    loginUrl.searchParams.set("redirect_to", window.location.href);
    window.location.href = loginUrl.toString();
  }, []);

  return null;
};

const isDevelopment =
  process.env.NODE_ENV === "development" ||
  window.location.hostname.includes("localhost") ||
  window.location.hostname.includes("127.0.0.1");
const isShopHost = window.location.hostname.includes("shop.ammachekku.com");
const shouldRedirect = !isDevelopment && !isShopHost;

function ShopRoute() {
  const location = useLocation();
  const searchQuery = new URLSearchParams(location.search).get("search")?.trim();

  return searchQuery ? <SearchResults /> : <Shop />;
}

function InnerRoutes() {
  const [navLoading, setNavLoading] = React.useState(true); // show on initial load
  const location = useLocation();

  // show a large spinner only the first time the loader is ever displayed
  const [firstVisit] = React.useState(() => !sessionStorage.getItem("loaderSeen"));

  React.useEffect(() => {
    if (!sessionStorage.getItem("loaderSeen")) {
      sessionStorage.setItem("loaderSeen", "1");
    }
  }, []);

  React.useEffect(() => {
    setNavLoading(true);
    // keep loader visible at least 500ms so it's noticeable
    const timer = setTimeout(() => setNavLoading(false), 500);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // large first appearance, smaller afterwards
  const loaderSize = firstVisit ? 250 : 150;

  return (
    <>
      {navLoading && <Loader size={loaderSize} />}
      <Suspense fallback={<Loader size={loaderSize} />}>
        <Routes>
          {/* externally hosted pages (redirect only if not on shop host or dev) */}
          <Route
            path="/"
            element={
              shouldRedirect ? <ExternalRedirect url="https://ammachekku.com" /> : <Shop />
            }
          />
          <Route
            path="/about"
            element={
              shouldRedirect ? <ExternalRedirect url="https://ammachekku.com/about/" /> : <Shop />
            }
          />
          <Route
            path="/contact-us"
            element={
              shouldRedirect ? <ExternalRedirect url="https://ammachekku.com/contact/" /> : <Shop />
            }
          />
          <Route
            path="/contact"
            element={
              shouldRedirect ? <ExternalRedirect url="https://ammachekku.com/contact" /> : <Shop />
            }
          />
          {/* shop route still exists in case anyone bookmarks it */}
          <Route path="/shop" element={<ShopRoute />} />
          <Route path="/category/:categorySlug" element={<CategoryPage />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/success" element={<StepSuccess />} />
          <Route path="/login" element={<WordpressLoginRedirect />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forget-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/my-account" element={<Myaccount />} />
          <Route path="/myaccount" element={<Navigate to="/my-account" replace />} />
          <Route path="/account" element={<Navigate to="/my-account" replace />} />
          <Route path="/account/*" element={<Navigate to="/my-account" replace />} />
          <Route path="/terms_and_conditions" element={<TermsConditions />} />
          <Route path="/privacy_and_policy" element={<PrivacyAndPolicy />} />
          <Route path="/shipping_policy" element={<ShippingPolicy />} />
          <Route
            path="/return_refund_policy"
            element={<ReturnRefundPolicy />}
          />
          {/* contact-us route removed - component unavailable */}
        </Routes>
      </Suspense>
    </>
  );
}

export default function Router() {
  return (
    <BrowserRouter>
      <InnerRoutes />
    </BrowserRouter>
  );
}
